import { Loader2, ScrollText } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { Text } from '@/components/ui/catalyst/text';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Z_INDEX_DROPDOWN_IN_MODAL,
  Z_INDEX_MODAL_NESTED,
} from '@/components/ui/zIndex';
import { firmsApiClient } from '@/services/api/firmsService';
import type { HipaaPackageSchema } from '@/services/api/hipaaPackageService';
import { useValidateAllComponentsMutation } from '@/services/api/hipaaPackageService';
import { mattersApiClient } from '@/services/api/mattersService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';

import { useRequestPage } from '../contexts/RequestPageContext';
import { SelectDestinationModal } from '../modals/SelectDestinationModal';
import { MemoizedIframePreview } from '../package-view/MemoizedIframePreview';
import type { ReviewSubmitFormState } from '../types';
import { DocumentPreviewModal } from './DocumentPreviewModal';
import {
  FollowUpStrategySection,
  toFollowUpChannel,
} from './FollowUpStrategySection';
import { PostTaskUpdateSection } from './PostTaskUpdateSection';
import { ReviewClientIdentificationSection } from './ReviewClientIdentificationSection';
import { ReviewSummaryTable } from './ReviewSummaryTable';
import { useReviewSubmitActions } from './useReviewSubmitActions';
import { getVisiblePackageComponents, isPackageRendering } from './utils';

type FirmFrontChannel = {
  id?: string | null;
  address?: string | null;
};

type EmailSenderOption = 'firm' | 'records';
type PendingOnlyOfficeSaveTarget = {
  componentId: string;
  modifiedAt: string | null;
  waitMode: 'grace' | 'strict';
};
type PackageComponentWithModifiedAt = NonNullable<
  HipaaPackageSchema['components']
>[number] & {
  modified_at?: string | null;
};

const RECORDS_SENDER_ADDRESS = 'records@finchlegal.com';
const RECORDS_FRONT_CHANNEL_ID = 'cha_j9s4e';
const ONLYOFFICE_SAVE_POLL_INTERVAL_MS = 500;
const ONLYOFFICE_SAVE_POLL_COUNT = 24;
const ONLYOFFICE_SAVE_GRACE_POLL_COUNT = 8;

export const ReviewSubmitPage = () => {
  const {
    matterId,
    providerId,
    provider,
    refetchRequest,
    refetchPackage,
    request,
    packageData,
    setViewMode,
    canEditPackage,
    hasMissingSignature,
    requestInfo,
    validationStatus,
  } = useRequestPage();

  const {
    ensureFinalPackagePdf,
    handleSubmit,
    handleDownloadAuthorization,
    isSubmitting,
    submitPhase,
  } =
    useReviewSubmitActions();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewInitialAddDocumentKind, setPreviewInitialAddDocumentKind] =
    useState<
      | 'coversheet'
      | 'authorization'
      | 'matter_document'
      | 'static_document'
      | 'manual_upload'
      | undefined
    >(undefined);
  const [isDestinationModalOpen, setIsDestinationModalOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isEmailSubmitModalOpen, setIsEmailSubmitModalOpen] = useState(false);
  const [isFinalPreviewOpen, setIsFinalPreviewOpen] = useState(false);
  const [isPreparingFinalPreview, setIsPreparingFinalPreview] = useState(false);
  const [finalPreviewUrl, setFinalPreviewUrl] = useState<string | null>(null);
  const [pendingOnlyOfficeSaveTargets, setPendingOnlyOfficeSaveTargets] =
    useState<PendingOnlyOfficeSaveTarget[] | null>(null);
  const [isWaitingForOnlyOfficeSave, setIsWaitingForOnlyOfficeSave] =
    useState(false);
  const [hasRefreshedValidation, setHasRefreshedValidation] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailSenderOption, setEmailSenderOption] =
    useState<EmailSenderOption>('firm');
  const [emailValidationError, setEmailValidationError] = useState<
    string | null
  >(null);
  const [emailTemplateInitialized, setEmailTemplateInitialized] =
    useState(false);

  const existingStrategy = request?.metadata?.transmission_strategy;
  const [validateAllComponents, { isLoading: isRefreshingValidation }] =
    useValidateAllComponentsMutation();
  const [formState, setFormState] = useState<ReviewSubmitFormState>(() => ({
    postTaskUpdate: {
      enabled: false,
      text: '',
    },
    followUpStrategy: {
      enabled: existingStrategy?.enabled ?? true,
      channel: toFollowUpChannel(existingStrategy?.channel),
      intervalDays: existingStrategy?.interval_days ?? 3,
      maxAttempts: existingStrategy?.max_attempts ?? 3,
    },
  }));

  const visibleComponents = useMemo(
    () => getVisiblePackageComponents(packageData?.components),
    [packageData?.components],
  );
  const hasPreviousPackageArtifact = Boolean(
    packageData?.completed_package_id ||
      packageData?.download_url ||
      packageData?.rendered_pdf_url,
  );
  const isPreviousPackageOnlyState =
    hasPreviousPackageArtifact && visibleComponents.length === 0;
  const { data: matterData } = mattersApiClient.useGetMatterQuery(matterId);
  const firmId = matterData?.firm?.id;
  const { data: firmData } = firmsApiClient.useGetFirmQuery(firmId || '', {
    skip: !firmId,
  });
  const firmFrontChannel = firmData?.front_channel as
    | FirmFrontChannel
    | null
    | undefined;
  const isManualDownloadFlow =
    request?.channel?.type === 'portal' || request?.channel?.type === 'mail';
  const isEmailChannel = request?.channel?.type === 'email';
  const hasPendingOnlyOfficeSave = pendingOnlyOfficeSaveTargets !== null;
  const isPackageGenerating = isPackageRendering(packageData);
  const hasFinalizablePackage =
    Boolean(packageData?.completed_package_id) ||
    Boolean(packageData?.download_url) ||
    Boolean(packageData?.rendered_pdf_url) ||
    Boolean(packageData?.components?.length);
  const canManageDocuments = canEditPackage || hasPreviousPackageArtifact;
  const canSubmit =
    Boolean(request?.channel?.type) &&
    (isManualDownloadFlow
      ? hasFinalizablePackage
      : hasFinalizablePackage &&
        !hasMissingSignature &&
        !validationStatus.hasMissingFields &&
        !isPackageGenerating &&
        !isRefreshingValidation);
  const isFollowUpEligible =
    request?.channel?.type === 'fax' || request?.channel?.type === 'email';
  const isFrontChannelMissing =
    isEmailChannel &&
    !firmFrontChannel?.address &&
    !RECORDS_FRONT_CHANNEL_ID.trim();
  const isFirmChannelMissing = isEmailChannel && !firmFrontChannel?.address;
  const {
    data: emailTemplate,
    isFetching: isEmailTemplateFetching,
    isLoading: isEmailTemplateLoading,
    isError: isEmailTemplateError,
    error: emailTemplateError,
  } = treatmentRecordRequestService.useGetHipaaEmailTemplateQuery(
    {
      matterId,
      providerId,
      requestId: request?.id || '',
    },
    {
      skip: !isEmailSubmitModalOpen || !request?.id,
    },
  );
  const downloadDescription = useMemo(() => {
    if (request?.channel?.type === 'portal') {
      return 'Portal submissions are handled outside Finch. Download the completed authorization PDF for portal upload.';
    }

    if (request?.channel?.type === 'mail') {
      return 'Mail submissions are handled outside Finch. Download the completed authorization PDF for printing or mailing.';
    }

    return 'Download the completed authorization PDF for review, sharing, or manual handling outside Finch.';
  }, [request?.channel?.type]);
  const finalPreviewContinueLabel = useMemo(() => {
    if (isEmailChannel) {
      return 'Continue to Email';
    }

    if (isManualDownloadFlow) {
      return 'Continue to Download';
    }

    return 'Submit Request';
  }, [isEmailChannel, isManualDownloadFlow]);
  const emailTemplateErrorMessage = useMemo(() => {
    if (!emailTemplateError) return null;
    if ('status' in (emailTemplateError as { status?: number })) {
      const { data } = emailTemplateError as {
        data?: { detail?: string; message?: string; error?: string };
      };
      return (
        data?.detail ||
        data?.message ||
        data?.error ||
        'Unable to load email template. You can still edit the message manually.'
      );
    }
    if ('message' in (emailTemplateError as { message?: string })) {
      return (
        (emailTemplateError as { message?: string }).message ||
        'Unable to load email template. You can still edit the message manually.'
      );
    }
    return 'Unable to load email template. You can still edit the message manually.';
  }, [emailTemplateError]);

  const isEmailTemplatePending =
    !emailTemplateInitialized &&
    (isEmailTemplateLoading || isEmailTemplateFetching);

  const submitLabel = useMemo(() => {
    if (submitPhase === 'finalizing') {
      return 'Preparing Package...';
    }

    if (submitPhase === 'downloading') {
      return isManualDownloadFlow ? 'Downloading...' : 'Preparing Download...';
    }

    if (submitPhase === 'submitting') {
      return 'Submitting...';
    }

    if (isManualDownloadFlow) {
      return 'Download';
    }

    switch (request?.channel?.type) {
      case 'fax':
        return 'Submit';
      case 'email':
        return 'Submit';
      default:
        return 'Submit';
    }
  }, [
    isManualDownloadFlow,
    request?.channel?.type,
    submitPhase,
  ]);
  const submitButtonLabel = useMemo(() => {
    if (isPreparingFinalPreview) {
      return 'Preparing Preview...';
    }

    if (isWaitingForOnlyOfficeSave) {
      return 'Saving Edits...';
    }

    return submitLabel;
  }, [isPreparingFinalPreview, isWaitingForOnlyOfficeSave, submitLabel]);

  useEffect(() => {
    setHasRefreshedValidation(false);
  }, [packageData?.id]);

  useEffect(() => {
    if (
      !packageData?.id ||
      hasRefreshedValidation ||
      isManualDownloadFlow ||
      packageData.package_source !== 'generated' ||
      packageData.status === 'published' ||
      packageData.status === 'generating'
    ) {
      return undefined;
    }

    let isCancelled = false;

    const refreshValidation = async () => {
      try {
        await validateAllComponents({
          ...requestInfo,
          packageId: packageData.id,
        }).unwrap();

        if (isCancelled) return;

        await refetchPackage();
        if (!isCancelled) {
          setHasRefreshedValidation(true);
        }
      } catch {
        if (!isCancelled) {
          toast.error('Failed to refresh package validation');
        }
      }
    };

    refreshValidation();

    return () => {
      isCancelled = true;
    };
  }, [
    hasRefreshedValidation,
    isManualDownloadFlow,
    packageData?.id,
    packageData?.package_source,
    packageData?.status,
    refetchPackage,
    requestInfo,
    validateAllComponents,
  ]);

  useEffect(() => {
    if (!isFollowUpEligible) {
      setFormState((current) => ({
        ...current,
        followUpStrategy: {
          ...current.followUpStrategy,
          enabled: false,
        },
      }));
    }
  }, [isFollowUpEligible]);

  useEffect(() => {
    if (!isEmailSubmitModalOpen) {
      setEmailSubject('');
      setEmailBody('');
      setEmailValidationError(null);
      setEmailTemplateInitialized(false);
      setEmailSenderOption('firm');
      return;
    }

    if (emailTemplate && !emailTemplateInitialized) {
      setEmailSubject(emailTemplate.subject);
      setEmailBody(emailTemplate.body);
      setEmailTemplateInitialized(true);
      setEmailValidationError(null);
    } else if (isEmailTemplateError && !emailTemplateInitialized) {
      setEmailTemplateInitialized(true);
    }
  }, [
    emailTemplate,
    emailTemplateInitialized,
    isEmailSubmitModalOpen,
    isEmailTemplateError,
  ]);

  useEffect(() => {
    if (!isEmailSubmitModalOpen) {
      return;
    }

    if (!firmFrontChannel?.address && RECORDS_FRONT_CHANNEL_ID.trim()) {
      setEmailSenderOption('records');
      return;
    }

    setEmailSenderOption('firm');
  }, [firmFrontChannel?.address, isEmailSubmitModalOpen]);

  const updatePostTaskEnabled = useCallback((enabled: boolean) => {
    setFormState((current) => ({
      ...current,
      postTaskUpdate: { ...current.postTaskUpdate, enabled },
    }));
  }, []);

  const updatePostTaskText = useCallback((text: string) => {
    setFormState((current) => ({
      ...current,
      postTaskUpdate: { ...current.postTaskUpdate, text },
    }));
  }, []);

  const updateFollowUpsEnabled = useCallback((enabled: boolean) => {
    setFormState((current) => ({
      ...current,
      followUpStrategy: { ...current.followUpStrategy, enabled },
    }));
  }, []);

  const updateFollowUpChannel = useCallback(
    (channel: 'fax' | 'email') => {
      setFormState((current) => ({
        ...current,
        followUpStrategy: { ...current.followUpStrategy, channel },
      }));
    },
    [],
  );

  const updateFollowUpInterval = useCallback((intervalDays: number) => {
    setFormState((current) => ({
      ...current,
      followUpStrategy: { ...current.followUpStrategy, intervalDays },
    }));
  }, []);

  const updateFollowUpAttempts = useCallback((maxAttempts: number) => {
    setFormState((current) => ({
      ...current,
      followUpStrategy: { ...current.followUpStrategy, maxAttempts },
    }));
  }, []);

  const handlePotentialOnlyOfficeSave = useCallback(
    (editedComponents: PendingOnlyOfficeSaveTarget[]) => {
      setPendingOnlyOfficeSaveTargets(editedComponents);
    },
    [],
  );

  const hasOnlyOfficeSaveCompleted = useCallback(
    (
      latestPackage: HipaaPackageSchema | undefined,
      pendingTargets: PendingOnlyOfficeSaveTarget[],
    ) => {
      if (!latestPackage) {
        return false;
      }

      const latestComponentsById = new Map(
        ((latestPackage.components ?? []) as PackageComponentWithModifiedAt[]).map(
          (component) => [
            component.id,
            component,
          ],
        ),
      );

      return pendingTargets.every(({ componentId, modifiedAt }) => {
        const latestComponent = latestComponentsById.get(componentId);
        if (!latestComponent?.modified_at) {
          return false;
        }

        if (!modifiedAt) {
          return true;
        }

        return latestComponent.modified_at > modifiedAt;
      });
    },
    [],
  );

  const waitForPendingOnlyOfficeSave = useCallback(async (): Promise<boolean> => {
    if (!pendingOnlyOfficeSaveTargets) {
      return true;
    }

    const hasStrictTargets = pendingOnlyOfficeSaveTargets.some(
      ({ waitMode }) => waitMode === 'strict',
    );
    const maxAttempts = hasStrictTargets
      ? ONLYOFFICE_SAVE_POLL_COUNT
      : ONLYOFFICE_SAVE_GRACE_POLL_COUNT;

    const pollForSavedEdit = async (attempt: number): Promise<boolean> => {
      try {
        const result = await refetchPackage();
        const latestPackage =
          (result.data as HipaaPackageSchema | undefined) ?? packageData;

        if (
          hasOnlyOfficeSaveCompleted(latestPackage, pendingOnlyOfficeSaveTargets)
        ) {
          setPendingOnlyOfficeSaveTargets(null);
          return true;
        }
      } catch {
        // Keep trying until the OnlyOffice callback finishes saving edits.
      }

      if (attempt >= maxAttempts) {
        if (!hasStrictTargets) {
          setPendingOnlyOfficeSaveTargets(null);
          return true;
        }

        return false;
      }

      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, ONLYOFFICE_SAVE_POLL_INTERVAL_MS);
      });
      return pollForSavedEdit(attempt + 1);
    };

    setIsWaitingForOnlyOfficeSave(true);

    try {
      const didSaveComplete = await pollForSavedEdit(1);
      if (!didSaveComplete) {
        toast.info(
          'Your document edits are still saving. Please wait a few seconds and try again.',
        );
      }
      return didSaveComplete;
    } finally {
      setIsWaitingForOnlyOfficeSave(false);
    }
  }, [
    hasOnlyOfficeSaveCompleted,
    packageData,
    pendingOnlyOfficeSaveTargets,
    refetchPackage,
  ]);

  const onSubmit = useCallback(async () => {
    const isOnlyOfficeSaveReady = await waitForPendingOnlyOfficeSave();
    if (!isOnlyOfficeSaveReady) {
      return;
    }

    if (isEmailChannel) {
      if (!request?.channel?.email?.trim()) {
        toast.info('Please add an email address before submitting.');
        return;
      }

      if (isFrontChannelMissing) {
        toast.error(
          'No email sender channel available. Please configure a firm Front channel or records channel.',
        );
        return;
      }
    }

    setIsPreparingFinalPreview(true);

    try {
      const finalPackage =
        packageData?.package_source === 'generated'
          ? await ensureFinalPackagePdf()
          : packageData;
      const previewUrl =
        finalPackage?.rendered_pdf_url ||
        finalPackage?.download_url ||
        packageData?.rendered_pdf_url ||
        packageData?.download_url;

      if (!previewUrl) {
        throw new Error('Preview not available');
      }

      setFinalPreviewUrl(previewUrl);
      setIsFinalPreviewOpen(true);
    } catch {
      toast.error('Failed to prepare the final PDF preview');
    } finally {
      setIsPreparingFinalPreview(false);
    }
  }, [
    ensureFinalPackagePdf,
    isEmailChannel,
    isFrontChannelMissing,
    packageData,
    request?.channel?.email,
    waitForPendingOnlyOfficeSave,
  ]);

  const onConfirmFinalPreview = useCallback(async () => {
    setIsFinalPreviewOpen(false);

    if (isEmailChannel) {
      setIsEmailSubmitModalOpen(true);
      return;
    }

    if (isManualDownloadFlow) {
      setIsDownloadModalOpen(true);
      return;
    }

    await handleSubmit(formState);
  }, [formState, handleSubmit, isEmailChannel, isManualDownloadFlow]);

  const onConfirmEmailSubmit = useCallback(async () => {
    const isOnlyOfficeSaveReady = await waitForPendingOnlyOfficeSave();
    if (!isOnlyOfficeSaveReady) {
      return;
    }

    const trimmedSubject = emailSubject.trim();
    const trimmedBody = emailBody.trim();

    if (!trimmedSubject || !trimmedBody) {
      setEmailValidationError('Subject and body are required.');
      return;
    }

    if (emailSenderOption === 'records' && !RECORDS_FRONT_CHANNEL_ID.trim()) {
      return;
    }

    if (emailSenderOption === 'firm' && !firmFrontChannel?.address) {
      return;
    }

    setEmailValidationError(null);

    const success = await handleSubmit(formState, {
      submitPayload: {
        email_subject: trimmedSubject,
        email_body: trimmedBody,
        ...(emailSenderOption === 'records'
          ? { front_channel_id: RECORDS_FRONT_CHANNEL_ID.trim() }
          : {}),
      },
    });

    if (success) {
      setIsEmailSubmitModalOpen(false);
    }
  }, [
    emailBody,
    emailSenderOption,
    emailSubject,
    firmFrontChannel?.address,
    formState,
    handleSubmit,
    waitForPendingOnlyOfficeSave,
  ]);

  const onDownloadOnly = useCallback(async () => {
    const isOnlyOfficeSaveReady = await waitForPendingOnlyOfficeSave();
    if (!isOnlyOfficeSaveReady) {
      return;
    }
    setIsDownloadModalOpen(false);
    await handleDownloadAuthorization(formState, { moveForward: false });
  }, [formState, handleDownloadAuthorization, waitForPendingOnlyOfficeSave]);

  const onDownloadAndAdvance = useCallback(async () => {
    const isOnlyOfficeSaveReady = await waitForPendingOnlyOfficeSave();
    if (!isOnlyOfficeSaveReady) {
      return;
    }
    setIsDownloadModalOpen(false);
    await handleDownloadAuthorization(formState, { moveForward: true });
  }, [formState, handleDownloadAuthorization, waitForPendingOnlyOfficeSave]);

  const documentSummaryText = useMemo(() => {
    if (isWaitingForOnlyOfficeSave) {
      return 'Applying your recent document edits before finalizing the package';
    }

    if (hasPendingOnlyOfficeSave) {
      return 'Recent document edits will be applied when you finalize the package';
    }

    if (isPreviousPackageOnlyState) {
      return 'Using previous HIPAA package';
    }

    return `${visibleComponents.length} document${
      visibleComponents.length === 1 ? '' : 's'
    } will be generated`;
  }, [
    hasPendingOnlyOfficeSave,
    isWaitingForOnlyOfficeSave,
    isPreviousPackageOnlyState,
    visibleComponents.length,
  ]);

  return (
    <div className="flex h-full justify-center bg-white font-sans">
      <div className="flex h-full w-full max-w-[832px] flex-col bg-white font-sans">
        <div className="border-b border-zinc-200 px-8 py-6">
          <div className="flex items-center justify-start">
            <Text className="font-sans text-[20px] font-semibold leading-7 text-zinc-950">
              Configure Requests for {provider?.name || 'Provider'}
            </Text>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-[768px] space-y-[18px] px-8 py-6">
            <ReviewSummaryTable
              onResolveSignature={() => {
                setPreviewInitialAddDocumentKind('matter_document');
                setIsPreviewOpen(true);
              }}
            />
            <ReviewClientIdentificationSection />

            <div className="-mt-2 flex items-center justify-between gap-6">
              <Button
                outline
                className="!items-center h-9 rounded-[10px] border-zinc-300 bg-white px-4 font-sans text-[14px] font-medium text-zinc-800 shadow-sm"
                onClick={() => {
                  setPreviewInitialAddDocumentKind(undefined);
                  setIsPreviewOpen(true);
                }}
                disabled={!canManageDocuments && visibleComponents.length === 0}
              >
                <ScrollText className="mr-1.5 h-[14px] w-[14px]" />
                Edit Documents
              </Button>
              <Text className="font-sans text-[13px] leading-4 text-zinc-400">
                {documentSummaryText}
              </Text>
            </div>

            <PostTaskUpdateSection
              enabled={formState.postTaskUpdate.enabled}
              text={formState.postTaskUpdate.text}
              onToggle={updatePostTaskEnabled}
              onTextChange={updatePostTaskText}
            />

            <FollowUpStrategySection
              enabled={formState.followUpStrategy.enabled}
              channel={formState.followUpStrategy.channel}
              intervalDays={formState.followUpStrategy.intervalDays}
              maxAttempts={formState.followUpStrategy.maxAttempts}
              disabled={!isFollowUpEligible}
              disabledMessage={
                !isFollowUpEligible
                  ? 'Automatic follow-ups are unavailable for portal and mail submissions.'
                  : undefined
              }
              onToggle={updateFollowUpsEnabled}
              onChannelChange={updateFollowUpChannel}
              onIntervalChange={updateFollowUpInterval}
              onMaxAttemptsChange={updateFollowUpAttempts}
            />

            <div className="h-6" />
          </div>
        </div>

        <div className="border-t border-zinc-200 px-8 py-4">
          <div className="mx-auto flex w-full max-w-[768px] items-center justify-between">
            <Button
              outline
              className="!items-center h-10 min-w-[78px] rounded-[10px] border-zinc-300 bg-white px-4 font-sans text-[14px] font-semibold text-zinc-800 shadow-sm"
              onClick={() => setViewMode('default')}
            >
              Legacy Mode
            </Button>

            <div className="flex items-center gap-3">
              <Button
                outline
                className="!items-center h-10 rounded-[10px] border-zinc-300 bg-white px-4 font-sans text-[14px] font-semibold text-zinc-800 shadow-sm"
                onClick={() => setIsDestinationModalOpen(true)}
              >
                Change Destination
              </Button>

              {!isManualDownloadFlow && (
                <Button
                  outline
                  className="!items-center h-10 rounded-[10px] border-zinc-300 bg-white px-4 font-sans text-[14px] font-semibold text-zinc-800 shadow-sm"
                  onClick={() => setIsDownloadModalOpen(true)}
                  disabled={
                    !hasFinalizablePackage ||
                    isSubmitting ||
                    isWaitingForOnlyOfficeSave
                  }
                >
                  Download
                </Button>
              )}

              <div className="relative">
                {isSubmitting && (
                  <>
                    <div className="absolute -inset-2 rounded-[16px] bg-emerald-400/20 blur-xl animate-pulse" />
                    <div className="absolute -inset-1 rounded-[14px] border border-emerald-300/50 shadow-[0_0_28px_rgba(52,211,153,0.45)] animate-pulse" />
                  </>
                )}
                <Button
                  color="green"
                  className="!items-center relative h-10 min-w-[217px] rounded-[10px] px-5 font-sans text-[14px] font-semibold shadow-sm transition-all duration-300"
                  disabled={
                    !canSubmit ||
                    isSubmitting ||
                    isWaitingForOnlyOfficeSave ||
                    isPreparingFinalPreview
                  }
                  onClick={onSubmit}
                >
                  {(isSubmitting ||
                    isWaitingForOnlyOfficeSave ||
                    isPreparingFinalPreview) && (
                    <Loader2 className="mr-2 h-[14px] w-[14px] animate-spin" />
                  )}
                  {submitButtonLabel}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <DocumentPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => {
          setIsPreviewOpen(false);
          setPreviewInitialAddDocumentKind(undefined);
        }}
        onPotentialOnlyOfficeSave={handlePotentialOnlyOfficeSave}
        initialAddDocumentKind={previewInitialAddDocumentKind}
      />

      {request && provider && (
        <SelectDestinationModal
          isOpen={isDestinationModalOpen}
          onClose={() => setIsDestinationModalOpen(false)}
          request={request}
          providerId={providerId}
          matterId={matterId}
          provider={provider}
          onSuccess={refetchRequest}
        />
      )}

      <Dialog
        open={isFinalPreviewOpen}
        onClose={() => {
          if (!isPreparingFinalPreview && !isSubmitting) {
            setIsFinalPreviewOpen(false);
          }
        }}
        size="5xl"
        nested
        zIndex={Z_INDEX_MODAL_NESTED}
        className="sm:max-w-[min(96vw,1800px)]"
      >
        <DialogBody className="mt-0 flex h-[min(94vh,1400px)] max-h-[min(94vh,1400px)] flex-col !p-0">
          <div className="flex h-full flex-col overflow-hidden">
            <div className="border-b border-zinc-200 px-6 py-4">
              <DialogTitle className="font-sans text-[18px] font-semibold text-zinc-950">
                Review Final PDF
              </DialogTitle>
              <DialogDescription className="mt-1 font-sans text-[14px] leading-6 text-zinc-500">
                This is the final authorization PDF that will be used if you continue.
              </DialogDescription>
            </div>

            <div className="min-h-0 flex-1 overflow-hidden bg-zinc-50 p-6">
              <div className="h-full overflow-hidden rounded-[18px] border border-zinc-200 bg-white p-3 shadow-[0_12px_40px_rgba(15,23,42,0.06)]">
                {finalPreviewUrl ? (
                  <MemoizedIframePreview
                    url={finalPreviewUrl}
                    title="Final HIPAA package preview"
                    assumePdf
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-sm text-zinc-500">
                    Preview unavailable.
                  </div>
                )}
              </div>
            </div>

            <DialogActions className="border-t border-zinc-200 bg-white px-6 py-4 sm:flex-row sm:justify-end">
              <Button
                outline
                className="!items-center h-10 rounded-[10px] border-zinc-300 bg-white px-4 font-sans text-[14px] font-semibold text-zinc-800 shadow-sm"
                onClick={() => setIsFinalPreviewOpen(false)}
                disabled={isSubmitting}
              >
                Back
              </Button>
              <Button
                color="green"
                className="!items-center h-10 rounded-[10px] px-4 font-sans text-[14px] font-semibold shadow-sm"
                onClick={onConfirmFinalPreview}
                disabled={isSubmitting}
              >
                {finalPreviewContinueLabel}
              </Button>
            </DialogActions>
          </div>
        </DialogBody>
      </Dialog>

      <Dialog
        open={isDownloadModalOpen}
        onClose={() => setIsDownloadModalOpen(false)}
        size="lg"
        nested
        zIndex={Z_INDEX_MODAL_NESTED}
        className="sm:max-w-[520px] !rounded-[18px] !p-6"
      >
        <DialogTitle className="font-sans text-[18px] font-semibold text-zinc-950">
          Download Authorization PDF?
        </DialogTitle>
        <DialogDescription className="font-sans text-[14px] leading-6 text-zinc-500">
          {downloadDescription}
        </DialogDescription>

        <DialogBody className="space-y-4">
          {!isFollowUpEligible && (
            <div className="rounded-[14px] border border-amber-200 bg-amber-50 px-4 py-3">
              <Text className="font-sans text-[13px] leading-5 text-amber-900">
                Automatic follow-ups will be disabled because this submission
                method is not eligible.
              </Text>
            </div>
          )}

          <div className="rounded-[14px] border border-zinc-200 bg-zinc-50 px-4 py-3">
            <Text className="font-sans text-[13px] leading-5 text-zinc-700">
              After downloading, you can either keep the request as-is or move
              it forward and add a task update now.
            </Text>
          </div>
        </DialogBody>

        <DialogActions className="mt-2 gap-3 sm:flex-row sm:justify-end">
          <Button
            outline
            className="!items-center h-10 rounded-[10px] border-zinc-300 bg-white px-4 font-sans text-[14px] font-semibold text-zinc-800 shadow-sm"
            onClick={() => setIsDownloadModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            color="green"
            className="!items-center h-10 rounded-[10px] px-4 font-sans text-[14px] font-semibold shadow-sm"
            onClick={onDownloadOnly}
            disabled={isSubmitting || isWaitingForOnlyOfficeSave}
          >
            Download Only
          </Button>
          <Button
            outline
            className="!items-center h-10 rounded-[10px] border-zinc-300 bg-white px-4 font-sans text-[14px] font-semibold text-zinc-800 shadow-sm"
            onClick={onDownloadAndAdvance}
            disabled={isSubmitting || isWaitingForOnlyOfficeSave}
          >
            Download and Move Forward
          </Button>
        </DialogActions>
      </Dialog>

      {isEmailChannel && (
        <Dialog
          open={isEmailSubmitModalOpen}
          onClose={() => {
            if (!isSubmitting) {
              setIsEmailSubmitModalOpen(false);
            }
          }}
          size="lg"
          nested
          zIndex={Z_INDEX_MODAL_NESTED}
        >
          <DialogBody>
            <DialogTitle className="font-sans text-[18px] font-semibold text-zinc-950">
              Send HIPAA Package by Email
            </DialogTitle>
            <DialogDescription className="font-sans text-[14px] leading-6 text-zinc-500">
              Review and customize the email before sending it to the provider.
            </DialogDescription>

            <div className="mt-4 space-y-4">
              {isEmailTemplatePending ? (
                <div className="flex items-center justify-center py-10 text-sm text-gray-600">
                  <div className="flex items-center gap-3">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
                    <span>Loading email template…</span>
                  </div>
                </div>
              ) : (
                <>
                  {emailTemplateErrorMessage && (
                    <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                      {emailTemplateErrorMessage}
                    </div>
                  )}

                  <div className="flex flex-col gap-2">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label
                      htmlFor="review-email-to"
                      className="text-sm font-medium text-gray-900"
                    >
                      To
                    </label>
                    <Input
                      id="review-email-to"
                      value={request?.channel?.email || 'No email configured'}
                      disabled
                      className="bg-gray-50 text-gray-700"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className="text-sm font-medium text-gray-900">
                      From
                    </label>
                    <Select
                      value={emailSenderOption}
                      onValueChange={(value) =>
                        setEmailSenderOption(value as EmailSenderOption)
                      }
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select sender" />
                      </SelectTrigger>
                      <SelectContent
                        className="border border-gray-200 bg-white"
                        style={{ zIndex: Z_INDEX_DROPDOWN_IN_MODAL }}
                      >
                        <SelectItem
                          value="firm"
                          disabled={!firmFrontChannel?.address}
                          className={!firmFrontChannel?.address ? 'opacity-50' : ''}
                        >
                          {firmFrontChannel?.address
                            ? `Firm (${firmFrontChannel.address})`
                            : 'Firm Front channel (not configured)'}
                        </SelectItem>
                        <SelectItem
                          value="records"
                          disabled={!RECORDS_FRONT_CHANNEL_ID.trim()}
                          className={!RECORDS_FRONT_CHANNEL_ID.trim() ? 'opacity-50' : ''}
                        >
                          {RECORDS_SENDER_ADDRESS}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {emailSenderOption === 'firm' &&
                      !firmFrontChannel?.address && (
                        <div className="text-xs text-red-600">
                          Configure firm Front channel in Firms to send emails
                        </div>
                      )}
                    {emailSenderOption === 'records' &&
                      !RECORDS_FRONT_CHANNEL_ID.trim() && (
                        <div className="text-xs text-red-600">
                          Records sender channel ID not configured
                        </div>
                      )}
                    {isFirmChannelMissing && emailSenderOption === 'records' && (
                      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                        No firm-specific Front channel is configured. Emails
                        will be sent from the shared records address. Consider
                        configuring a firm-specific channel in Firm settings for
                        a more personalized sender.
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label
                      htmlFor="review-email-subject"
                      className="text-sm font-medium text-gray-900"
                    >
                      Subject
                    </label>
                    <Input
                      id="review-email-subject"
                      value={emailSubject}
                      onChange={(e) => {
                        setEmailSubject(e.target.value);
                        if (emailValidationError) {
                          setEmailValidationError(null);
                        }
                      }}
                      placeholder="Email subject"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label
                      htmlFor="review-email-body"
                      className="text-sm font-medium text-gray-900"
                    >
                      Body
                    </label>
                    <Textarea
                      id="review-email-body"
                      value={emailBody}
                      onChange={(e) => {
                        setEmailBody(e.target.value);
                        if (emailValidationError) {
                          setEmailValidationError(null);
                        }
                      }}
                      rows={8}
                      placeholder="Email body"
                    />
                  </div>

                  {emailValidationError && (
                    <div className="text-xs text-red-600">
                      {emailValidationError}
                    </div>
                  )}
                </>
              )}
            </div>

            <DialogActions>
              <Button
                color="zinc"
                onClick={() => setIsEmailSubmitModalOpen(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                color="indigo-steel"
                onClick={onConfirmEmailSubmit}
                disabled={isSubmitting || isEmailTemplatePending}
              >
                Confirm & Send Email
              </Button>
            </DialogActions>
          </DialogBody>
        </Dialog>
      )}
    </div>
  );
};
